import { EVENT_UPDATED, EVENT_LOADED, EVENT_NEW, EVENT_DELETED, EVENT_ROOM_NAME_CHANGED, EVENT_MODE_RESET } from '../core/events.js';
import { EVENT_CORNER_ATTRIBUTES_CHANGED, EVENT_WALL_ATTRIBUTES_CHANGED, EVENT_ROOM_ATTRIBUTES_CHANGED,EVENT_ROOM_METADATA_CHANGED, EVENT_MOVED, EVENT_NEW_ROOMS_ADDED } from '../core/events.js';
import { EventDispatcher, Vector2, Vector3, Matrix4 } from 'three';
import { Utils } from '../core/utils.js';
import { Dimensioning } from '../core/dimensioning.js';
import { dimInch, dimFeetAndInch, dimMeter, dimCentiMeter, dimMilliMeter } from '../core/constants.js';
import { WallTypes,roomTypesColor,roomTypes } from '../core/constants.js';
import { Version } from '../core/version.js';
import { cornerTolerance, Configuration, configDimUnit, gridSize, fontSize } from '../core/configuration.js';


import { HalfEdge } from './half_edge.js';
import { Corner } from './corner.js';
import { Wall } from './wall.js';
import { Room } from './room.js';
import { CornerGroups } from './cornergroups.js';
import { Icon } from './icon.js';
import { Label } from './label.js';
import { Arrow } from './arrow.js';
import Boundary from './boundary.js';



const defaultRoomData = {name : "A New Area", type : "non", code : "none", factor:1, baseColor : "#AA336A", isDelete:false };
// import { Labels } from '../view/Labels.js';

/** */
export const defaultFloorPlanTolerance = 10.0;

/**
 * A Floorplan represents a number of Walls, Corners and Rooms.
 */
export class Floorplan extends EventDispatcher {
    /** Constructs a floorplan. */
    constructor() {
        super();
        /**
         * List of elements of Wall instance
         * 
         * @property {Wall[]} walls Array of walls
         * @type {Wall[]}
         */
        this.walls = [];
        this.icons = [];
        this.labels = [];
        this.arrows = [];
        this.__orphanWalls = [];

        this.__cacheQueue = [];

        this.__undoQueue = [];
        /**
         * List of elements of Corner instance
         * 
         * @property {Corner[]} corners array of corners
         * @type {Corner[]}
         */
        this.corners = [];

        /**
         * List of elements of Room instance
         * 
         * @property {Room[]} walls Array of walls
         * @type {Room[]}
         */
        this.rooms = [];
        this.rooms1 = [];

        this.roomCorners = [];

        this.__boundary = new Boundary(this);

        this.__externalCorners = [];
        this.__externalWalls = [];
        this.__externalRooms = [];

        /**
         * An {@link Object} that stores the metadata of rooms like name
         * 
         * @property {Object} metaroomsdata stores the metadata of rooms like
         *           name
         * @type {Object}
         */
        this.metaroomsdata = {};
        this.oldmetaroomsdata = {};
        // List with reference to callback on a new wall insert event
        /**
         * @deprecated
         */
        this.new_wall_callbacks = [];
        // List with reference to callbacks on a new corner insert event
        /**
         * @deprecated
         */
        this.new_corner_callbacks = [];
        // List with reference to callbacks on redraw event
        /**
         * @deprecated
         */
        this.redraw_callbacks = [];
        // List with reference to callbacks for updated_rooms event
        /**
         * @deprecated
         */
        this.updated_rooms = [];
        // List with reference to callbacks for roomLoaded event
        /**
         * @deprecated
         */
        this.roomLoadedCallbacks = [];

        this.floorTextures = {};
        /**
         * The {@link CarbonSheet} that handles the background image to show in
         * the 2D view
         * 
         * @property {CarbonSheet} _carbonSheet The carbonsheet instance
         * @type {Object}
         */
        this._carbonSheet = null;

        /**
         * This is necessary to sometimes explicitly stop the floorplan from doing
         * heavy computations
         */
        this.__updatesOn = true;

        this.__cornerGroups = new CornerGroups(this);

        this.__wallAttributesChangedEvent = this.__wallAttributesChanged.bind(this);
        this.__wallDeletedEvent = this.__wallDeleted.bind(this);
        this.__cornerAttributesChangedOrMovedEvent = this.__cornerAttributesChangedOrMoved.bind(this);
        this.__cornerDeletedEvent = this.__cornerDeleted.bind(this);
        this.__roomUpdateMetaDataEvent = this.__roomUpdateMetaData.bind(this);
        this.addEventListener(EVENT_ROOM_METADATA_CHANGED,this.__roomUpdateMetaDataEvent);
    }

    externalWallEdges() {
        let edges = [];
        this.__externalWalls.forEach((wall) => {
            if (wall.frontEdge) {
                edges.push(wall.frontEdge);
            }
            if (wall.backEdge) {
                edges.push(wall.backEdge);
            }
        });
        return edges;
    }
    /**
     * @return {HalfEdge[]} edges The array of {@link HalfEdge}
     */
    wallEdges() {
        var edges = [];
        this.walls.forEach((wall) => {
            if (wall.frontEdge) {
                edges.push(wall.frontEdge);
            }
            if (wall.backEdge) {
                edges.push(wall.backEdge);
            }
        });
        return edges;
    }

    __wallDeleted(evt) {
        let wall = evt.item;
        wall.removeEventListener(EVENT_DELETED, this.__wallDeletedEvent);
        wall.removeEventListener(EVENT_WALL_ATTRIBUTES_CHANGED, this.__wallAttributesChangedEvent);
        this.removeWall(wall);
    }

    __wallAttributesChanged(evt) {
        this.dispatchEvent(evt);
    }

    __cornerDeleted(evt) {
        let corner = evt.item;

        corner.removeEventListener(EVENT_DELETED, this.__cornerDeletedEvent);
        corner.removeEventListener(EVENT_CORNER_ATTRIBUTES_CHANGED, this.__cornerAttributesChangedOrMovedEvent);
        corner.removeEventListener(EVENT_MOVED, this.__cornerAttributesChangedOrMovedEvent);

        this.removeCorner(corner);
        this.update();
        this.dispatchEvent({ type: EVENT_DELETED, item: this });
    }

    __cornerAttributesChangedOrMoved(evt) {
        this.dispatchEvent(evt);
        let updateCorners = evt.item.adjacentCorners();
        updateCorners.push(evt.item);
        this.update(false, updateCorners);
    }

    fireOnNewWall(callback) {
        this.new_wall_callbacks.add(callback);
    }

    fireOnNewCorner(callback) {
        this.new_corner_callbacks.add(callback);
    }

    fireOnRedraw(callback) {
        this.redraw_callbacks.add(callback);
    }

    fireOnUpdatedRooms(callback) {
        this.updated_rooms.add(callback);
    }

    // This method needs to be called from the 2d floorplan whenever
    // the other method newWall is called.
    // This is to ensure that there are no floating walls going across
    // other walls. If two walls are intersecting then the intersection point
    // has to create a new wall.
    /**
     * Checks existing walls for any intersections they would make. If there are
     * intersections then introduce new corners and new walls as required at
     * places
     * 
     * @param {Corner}
     *            start
     * @param {Corner}
     *            end
     * @return {boolean} intersects
     */

    newWallsForIntersections(start, end) {
        var intersections = false;
        // This is a bug in the logic
        // When creating a new wall with a start and end
        // it needs to be checked if it is cutting other walls
        // If it cuts then all those walls have to removed and introduced as
        // new walls along with this new wall
        var cStart = new Vector2(start.x, start.y);
        var cEnd = new Vector2(end.x, end.y);
        var line = { p1: cStart, p2: cEnd };
        var newCorners = [];

        for (var i = 0; i < this.walls.length; i++) {
            var twall = this.walls[i];
            var bstart = { x: twall.getStartX(), y: twall.getStartY() };
            var bend = { x: twall.getEndX(), y: twall.getEndY() };
            var iPoint;
            iPoint = Utils.lineLineIntersectPoint(cStart, cEnd, bstart, bend);
            if (iPoint) {
                var nCorner = this.newCorner(iPoint.x, iPoint.y);
                newCorners.push(nCorner);
                nCorner.mergeWithIntersected(false);
                intersections = true;
            }
        }
        this.update();

        return intersections;
    }

    /**
     * Creates a new wall.
     * 
     * @param {Corner}
     *            start The start corner.
     * @param {Corner}
     *            end The end corner.
     * @returns {Wall} The new wall.
     */
    newWall(start, end, a, b,queuePush) {
        var scope = this;
        var wall = new Wall(start, end, a, b);

        this.walls.push(wall);

        if(queuePush){
            this.__undoQueue.push(wall);
        }
        
    
        wall.addEventListener(EVENT_DELETED, this.__wallDeletedEvent);
        wall.addEventListener(EVENT_WALL_ATTRIBUTES_CHANGED, this.__wallAttributesChangedEvent);

        this.dispatchEvent({ type: EVENT_NEW, item: this, newItem: wall });
        this.update();
        return wall;
    }



    /**
     * Creates a new corner.
     * 
     * @param {Number}
     *            x The x coordinate.
     * @param {Number}
     *            y The y coordinate.
     * @param {String}
     *            id An optional id. If unspecified, the id will be created
     *            internally.
     * @returns {Corner} The new corner.
     */
    newCorner(x, y, id) {
        let maxId = this.getCornerIDMaxValue();
        var scope = this;
        let id1 = (id) ? parseInt(id) : maxId + 1;
        var corner = new Corner(this, x, y, id1);
        for (var i = 0; i < this.corners.length; i++) {
            var existingCorner = this.corners[i];
            if (existingCorner.distanceFromCorner(corner) < cornerTolerance) {
                // this.__undoQueue.push(existingCorner);
                this.dispatchEvent({ type: EVENT_NEW, item: this, newItem: existingCorner });
                return existingCorner;
            }
        }
        
        this.corners.push(corner);
        
        corner.addEventListener(EVENT_DELETED, this.__cornerDeletedEvent);
        corner.addEventListener(EVENT_CORNER_ATTRIBUTES_CHANGED, this.__cornerAttributesChangedOrMovedEvent);
        corner.addEventListener(EVENT_MOVED, this.__cornerAttributesChangedOrMovedEvent);
        this.dispatchEvent({ type: EVENT_NEW, item: this, newItem: corner });

        // This code has been added by #0K. There should be an update whenever a
        // new corner is inserted
        this.update();
        return corner;
    }

    /**
     * create a Icon
     */

    newIcon(data){
        var icon = new Icon(data);
        this.icons.push(icon);
        this.update();
        return icon;
    }

    /**
     * create a Custom Label
     */

    newLabel(data){
        var label = new Label(data);
        this.labels.push(label);
        this.update();
        return label;
    }

    /**
     * create a New Arrow
     */
    newArrow(start,end){
        var arrow = new Arrow(start,end);
        this.arrows.push(arrow);
        this.update();
        
    }

    /**
     * Removes a wall.
     * 
     * @param {Wall}
     *            wall The wall to be removed.
     */
    removeWall(wall) {
        Utils.removeValue(this.walls, wall);
        this.update();
        this.dispatchEvent({ type: EVENT_DELETED, item: this, deleted: wall, item_type: 'wall' });
    }

    /**
     * Removes a corner.
     * 
     * @param {Corner}
     *            corner The corner to be removed.
     */
    removeCorner(corner) {
        Utils.removeValue(this.corners, corner);
        // this.update();
        this.dispatchEvent({ type: EVENT_DELETED, item: this, deleted: corner, item_type: 'corner' });
    }


    /**
     * Remove a Icon
     */

    removeIcon(icon){
        Utils.removeValue(this.icons, icon);
        this.dispatchEvent({ type: EVENT_DELETED, item: this, deleted: icon, item_type: 'icons' });
    }

    /**
     * Remove a Label
     */

    removeLabel(label){
        Utils.removeValue(this.labels, label);
        this.dispatchEvent({ type: EVENT_DELETED, item: this, deleted: label, item_type: 'labels' });
    }

    /**
     * Remove a Arrow
     */

    removeArrow(arrow){
        Utils.removeValue(this.arrows, arrow);
        this.dispatchEvent({ type: EVENT_DELETED, item: this, deleted: arrow, item_type: 'arrows' });
    }

    /**
     * Gets the walls.
     * 
     * @return {Wall[]}
     */
    getWalls() {
        return this.walls;
    }

    /**
     * Gets the corners.
     * 
     * @return {Corner[]}
     */
    getCorners() {
        return this.corners;
    }

    /**
     * Gets the rooms.
     * 
     * @return {Room[]}
     */
    getRooms() {
        return this.rooms;
    }

    /**
     * Gets the room overlapping the location x, y.
     * 
     * @param {Number}
     *            mx
     * @param {Number}
     *            my
     * @return {Room}
     */
    overlappedRoom(mx, my) {
        for (var i = 0; i < this.rooms.length; i++) {
            var room = this.rooms[i];
            var flag = room.pointInRoom(new Vector2(mx, my));
            if (flag) {
                return room;
            }
        }

        return null;
    }

    /**
     * Gets the Control of a Curved Wall overlapping the location x, y at a
     * tolerance.
     * 
     * @param {Number}
     *            x
     * @param {Number}
     *            y
     * @param {Number}
     *            tolerance
     * @return {Corner}
     */
    overlappedControlPoint(wall, x, y, tolerance) {
        tolerance = tolerance || defaultFloorPlanTolerance * 5;
        if (wall.a.distanceTo(new Vector2(x, y)) < tolerance && wall.wallType === WallTypes.CURVED) {
            return wall.a;
        } else if (wall.b.distanceTo(new Vector2(x, y)) < tolerance && wall.wallType === WallTypes.CURVED) {
            return wall.b;
        }

        return null;
    }

    /**
     * Gets the Corner overlapping the location x, y at a tolerance.
     * 
     * @param {Number}
     *            x
     * @param {Number}
     *            y
     * @param {Number}
     *            tolerance
     * @return {Corner}
     */
    overlappedCorner(x, y, tolerance) {
        tolerance = tolerance || defaultFloorPlanTolerance;
        for (var i = 0; i < this.corners.length; i++) {
            if (this.corners[i].distanceFrom(new Vector2(x, y)) < tolerance) {
                return this.corners[i];
            }
        }
        return null;
    }

    /**
     * Gets the Wall overlapping the location x, y at a tolerance.
     * 
     * @param {Number}
     *            x
     * @param {Number}
     *            y
     * @param {Number}
     *            tolerance
     * @return {Wall}
     */
    overlappedWall(x, y, tolerance) {
        tolerance = tolerance || defaultFloorPlanTolerance;
        for (var i = 0; i < this.walls.length; i++) {
            var newtolerance = tolerance; // (tolerance+
            // ((this.walls[i].wallType ==
            // WallTypes.CURVED)*tolerance*10));
            if (this.walls[i].distanceFrom(new Vector2(x, y)) < newtolerance) {
                return this.walls[i];
            }
        }
        return null;
    }

    /**
     * The metadata object with information about the rooms.
     * 
     * @return {Object} metaroomdata an object with room corner ids as key and
     *         names as values
     */
    getMetaRoomData() {
        var metaRoomData = {};
        this.rooms.forEach((room) => {
            var metaroom = {};
            var ids = room.roomByCornersId;
            metaroom['name'] = room.name;
            metaroom['factor'] = room.factor;
            metaroom['area'] = room.area;
            metaRoomData[ids] = metaroom;
        });
        return metaRoomData;
    }

    getIconData(){
        let iconData = [];
        this.icons.forEach((icon) => {
            let metaicon = {};
            metaicon = icon.metadata();
            iconData.push(metaicon);
        });
        return iconData;
    }

    getLabelData(){
        let labelData = [];
        this.labels.forEach((label) => {
            let metalabel = {};
            metalabel = label.metadata();
            labelData.push(metalabel);
        });
        return labelData;
    }

    getArrowData(){
        let arrowData = [];
        this.arrows.forEach((arrow) => {
            let metaarrow = {};
            metaarrow = arrow.metadata();
            arrowData.push(metaarrow);
        });
        return arrowData;
        
    }

    undoActions(){
        let undoItem = this.__undoQueue[this.__undoQueue.length - 1];
        undoItem.remove();
        this.__undoQueue.pop();
    }

    undoLastActionPoint(){
        return this.__undoQueue[this.__undoQueue.length - 1];
    }

    getCornerIDMaxValue(){
        let ids = [];let i=0;
        this.corners.forEach((corner) => {
            ids.push(parseFloat(corner.id));
        });
        
        return (ids.length > 0) ? Math.max.apply(Math, ids) : 0;
    }


    getRoomDataJson() {
        var metaRoomData = [];
        let i=0;let total = 0;
        this.rooms.forEach((room) => {
            var metaroom = [];
            var ids = room.roomByCornersId;
            // let area = (Math.round(this.__room.area * 100) / 100) * roomFactor;
            let area = (Math.round(room.area * 100) / 100) * parseFloat(room.factor);
            let area_T = Dimensioning.cmToMeasure(area.toFixed(1), 2);
            if(room.roomType === 'Living Area'){
                total += parseFloat(area_T);
            }
            
            metaroom[0] = room.code;
            metaroom[1] = room.name;
            metaroom[2] = parseFloat(room.factor);
            metaroom[3] = area_T;
            metaroom[4] = area_T;
            metaRoomData[i] = metaroom;
            i++;
        });
        // total = Math.round(Dimensioning.cmToMeasureRaw_D(total.toFixed(1),2));
        return {rooms : metaRoomData, total };
    }

    __roomUpdateMetaData(o){
        let scope = this;
        let i = 0;
        var room = o.item;
        

        if (scope.metaroomsdata[room.roomByCornersId]) {
            scope.metaroomsdata[room.roomByCornersId]['name'] = room.name;
            scope.metaroomsdata[room.roomByCornersId]['factor'] = room.factor;
            scope.metaroomsdata[room.roomByCornersId]['roomType'] = room.roomType;
            scope.metaroomsdata[room.roomByCornersId]['baseColor'] = room.baseColor;  
            scope.metaroomsdata[room.roomByCornersId]['code'] = room.code; 
            scope.metaroomsdata[room.roomByCornersId]['isDelete'] = room.isDelete;   
            scope.metaroomsdata[room.roomByCornersId]['center'] = room.areaCenter;   
        } else {
            scope.metaroomsdata[room.roomByCornersId] = {};
            scope.metaroomsdata[room.roomByCornersId]['name'] = room.name;
            scope.metaroomsdata[room.roomByCornersId]['factor'] = room.factor;
            scope.metaroomsdata[room.roomByCornersId]['roomType'] = room.roomType;
            scope.metaroomsdata[room.roomByCornersId]['baseColor'] = room.baseColor;
            scope.metaroomsdata[room.roomByCornersId]['code'] = room.code;
            scope.metaroomsdata[room.roomByCornersId]['isDelete'] = room.isDelete;
            scope.metaroomsdata[room.roomByCornersId]['center'] = room.areaCenter; 
        }
        for (; i < room.corners.length; i++) {
            let c = room.corners[i];
            c.code = room.code;
            c.roomData = scope.metaroomsdata[room.roomByCornersId];
        }
        this.update();
        room.selected = true;
        
        // this.dispatchEvent({ type: EVENT_UPDATED, item: room });
    }

    // Save the floorplan as a json object file
    /**
     * @return {void}
     */
    saveFloorplan() {
        var floorplans = { version: Version.getTechnicalVersion(), corners: {}, walls: [], rooms: {} };
        var cornerIds = [];
        // writing all the corners based on the corners array
        // is having a bug. This is because some walls have corners
        // that aren't part of the corners array anymore. This is a quick fix
        // by adding the corners to the json file based on the corners in the walls

        this.walls.forEach((wall) => {
            if (wall.getStart() && wall.getEnd()) {
                floorplans.walls.push({
                    'corner1': wall.getStart().id,
                    'corner2': wall.getEnd().id,
                    'wallType': wall.wallType.description,
                    'a': { x: wall.a.x, y: wall.a.y },
                    'b': { x: wall.b.x, y: wall.b.y },
                    'thickness': Dimensioning.cmToMeasureRaw(wall.thickness),
                    'isDelete': wall.isDelete,
                });
                cornerIds.push(wall.getStart());
                cornerIds.push(wall.getEnd());
            }
        });

        cornerIds.forEach((corner) => {
            floorplans.corners[corner.id] = { 
                'x': Dimensioning.cmToMeasureRaw(corner.x), 
                'y': Dimensioning.cmToMeasureRaw(corner.y), 
                'elevation': Dimensioning.cmToMeasureRaw(corner.elevation),
                'code':corner.code,
                'roomData':corner.roomData
            };
        });

        floorplans.rooms = this.metaroomsdata;
        floorplans.icons = this.getIconData();
        floorplans.labels = this.getLabelData();
        floorplans.arrows = this.getArrowData();
        floorplans.roomdata = this.metaroomsdata;
        floorplans.units = Configuration.getStringValue(configDimUnit);
        floorplans.gridsize = Configuration.getNumericValue(gridSize);
        floorplans.fontsize = Configuration.getNumericValue(fontSize);
        return floorplans;
    }

    // Load the floorplan from a previously saved json object file
    /**
     * @param {JSON}
     *            floorplan
     * @return {void}
     * @emits {EVENT_LOADED}
     */
    loadFloorplan(floorplan) {
        this.reset();
        this.__updatesOn = false;
        let corners = {};
        if (floorplan === null || !('corners' in floorplan) || !('walls' in floorplan)) {
            return;
        }
        let currentUnit = Configuration.getStringValue(configDimUnit);
        if (floorplan.units) {
            switch (floorplan.units) {
                case dimInch:
                    Configuration.setValue(configDimUnit, dimInch);
                    break;
                case dimFeetAndInch:
                    Configuration.setValue(configDimUnit, dimFeetAndInch);
                    break;
                case dimMeter:
                    Configuration.setValue(configDimUnit, dimMeter);
                    break;
                case dimCentiMeter:
                    Configuration.setValue(configDimUnit, dimCentiMeter);
                    break;
                case dimMilliMeter:
                    Configuration.setValue(configDimUnit, dimMilliMeter);
                    break;
            }
        }

        if(floorplan.gridsize){
            Configuration.setValue(gridSize, floorplan.gridsize);
        }

        if(floorplan.fontsize){
            Configuration.setValue(fontSize, floorplan.fontsize);
        }

        for (let id in floorplan.corners) {
            let corner = floorplan.corners[id];
            corners[id] = this.newCorner(Dimensioning.cmFromMeasureRaw(corner.x), Dimensioning.cmFromMeasureRaw(corner.y), id);
            if (corner.elevation) {
                corners[id].elevation = Dimensioning.cmFromMeasureRaw(corner.elevation);
            }
            if (corner.code) {
                corners[id].code = corner.code;
            }
            if (corner.roomData) {
                corners[id].roomData = corner.roomData;
            }
        }
        let scope = this;
        floorplan.walls.forEach((wall) => {

            let newWall = scope.newWall(corners[wall.corner1], corners[wall.corner2]);
           
            if (wall.thickness) {
                newWall.thickness = Dimensioning.cmFromMeasureRaw(wall.thickness);
            }
            // Adding of a, b, wallType (straight, curved) for walls happened
            newWall.a = wall.a;
            newWall.b = wall.b;
            newWall.isDelete = wall.isDelete;
            if (wall.wallType === 'CURVED') {
                newWall.wallType = WallTypes.CURVED;
            } else {
                newWall.wallType = WallTypes.STRAIGHT;
            }
            
        });

        this.__updatesOn = true;
        this.metaroomsdata = floorplan.rooms;
        this.oldmetaroomsdata = floorplan.roomdata;
        this.icons = floorplan.icons || [];
        this.labels = floorplan.labels || [];
        this.arrows = floorplan.arrows || [];
        this.update();
        this.dispatchEvent({ type: EVENT_LOADED, item: this });
    }

    
   
    /**
     * Resets the floorplan data to empty
     * 
     * @return {void}
     */
    reset() {
        let tmpCorners = this.corners.slice(0);
        let tmpWalls = this.walls.slice(0);
        let tmpRooms = this.rooms.slice(0);
        tmpRooms.forEach((room) =>{
            room.destroy()
        });
        tmpWalls.forEach((wall) => {
            wall.remove();
        });
        tmpCorners.forEach((corner) => {
            corner.remove();
        });
        this.corners = [];
        this.walls = [];
        this.rooms = [];
        this.icons = [];
        this.labels = [];
        this.arrows = [];
        this.icons.length = 0;
        this.labels.length = 0;
        this.arrows.length = 0;
        this.metaroomsdata = {};
        this.__externalRooms = [];
        this.__externalCorners = [];
        this.__externalWalls = [];
        this.update();
        this.dispatchEvent({ type: EVENT_MODE_RESET });
    }

    /**
     * @param {Object}
     *            event
     * @listens {EVENT_ROOM_NAME_CHANGED} When a room name is changed and
     *          updates to metaroomdata
     */
    roomNameChanged(e) {
        if (this.metaroomsdata) {
            this.metaroomsdata[e.item.roomByCornersId] = e.newname;
        }
    }

    /**
     * Returns the center of the floorplan in the y plane
     * 
     * @return {Vector2} center
     * @see https://threejs.org/docs/#api/en/math/Vector2
     */
    getCenter() {
        return this.getDimensions(true);
    }

    /**
     * Returns the bounding volume of the full floorplan
     * 
     * @return {Vector3} size
     * @see https://threejs.org/docs/#api/en/math/Vector3
     */
    getSize() {
        return this.getDimensions(false);
    }

    getSize3() {
        let size2D = this.getDimensions();
        let size3D = new Vector3(size2D.x, size2D.z, -Number.MAX_VALUE);
        for (let i = 0; i < this.corners.length; i++) {
            let corner = this.corners[i];
            size3D.z = Math.max(size3D.z, corner.elevation);
        }
        return size3D;
    }

    setSize(newSize) {
        let i = 0;
        let m = new Matrix4();
        let currentSize = this.getSize3();
        let scale = newSize.clone().divide(currentSize);
        m.scale(scale);
        for (; i < this.corners.length; i++) {
            let corner = this.corners[i];
            let vector = new Vector3(corner.location.x, corner.location.y, corner.elevation);
            vector = vector.applyMatrix4(m);
            corner.elevation = vector.z;
            corner.move(vector.x, vector.y);
        }
    }


    /**
     * Returns the bounding size or the center location of the full floorplan
     * 
     * @param {boolean}
     *            center If true return the center else the size
     * @return {Vector3} size
     * @see https://threejs.org/docs/#api/en/math/Vector3
     */
    getDimensions(center) {
        center = center || false; // otherwise, get size
        let infinity = 1.0e10;
        let bounds = this.getFloorBounds();
        let minBound = bounds['min'];
        let maxBound = bounds['max'];

        let xMin = minBound.x;
        let xMax = maxBound.x;
        let zMin = minBound.z;
        let zMax = maxBound.z;
        let ret;
        if (xMin === infinity || xMax === -infinity || zMin === infinity || zMax === -infinity) {
            ret = new Vector3();
        } else {
            if (center) {
                // center
                ret = new Vector3(xMin + ((xMax - xMin) * 0.5), 0, zMin + ((zMax - zMin) * 0.5));
            } else {
                // size
                ret = new Vector3((xMax - xMin), 0, (zMax - zMin));
            }
        }
        return ret;
    }

    getFloorBounds(){
        let infinity = 1.0e7;
        let minBounds = new Vector3(infinity, 0, infinity);
        let maxBounds = new Vector3(-infinity, -infinity, -infinity);
        this.corners.forEach((corner) => {
            minBounds.x = Math.min(minBounds.x, corner.x);
            // minBounds.y = Math.min(minBounds.y, corner.elevation);
            minBounds.z = Math.min(minBounds.z, corner.y);

            maxBounds.x = Math.max(maxBounds.x, corner.x);
            maxBounds.y = Math.max(maxBounds.y, corner.elevation);
            maxBounds.z = Math.max(maxBounds.z, corner.y);
        });

        return {'min': minBounds, 'max':maxBounds};
    }

    /**
     * An internal cleanup method
     */
    assignOrphanEdges() {
        let scope = this;
        function isInRoom(start, end){
            let insideTheRoom = null;
            scope.rooms.forEach((room) =>{
                let polygon = room.roomCornerPoints;
                let isInsideStart = Utils.pointInPolygon2(start, polygon);
                let isInsideEnd = Utils.pointInPolygon2(end, polygon);
                if(isInsideStart || isInsideEnd){
                    insideTheRoom = room;
                }
            });
            return insideTheRoom;
        }
        // kinda hacky
        // find orphaned wall segments (i.e. not part of rooms) and
        // give them edges
        let orphanWalls = [];
        let iterateWalls = function(wall){
            
        }.bind(this);

        this.walls.forEach((wall)=>{
            if (!wall.backEdge && !wall.frontEdge) {
                /**
                 * It is necessary to guess which room might contain this orphan wall
                 */
                let room = isInRoom(wall.start, wall.end);                
                let back = new HalfEdge(room, wall, false);
                let front = new HalfEdge(room, wall, true);
                wall.orphan = true;
                back.generatePlane();
                front.generatePlane();
                orphanWalls.push(wall);
            }
        });
        return orphanWalls;
    }

    /**
     * Update the floorplan with new rooms, remove old rooms etc.
     * //Should include for , updatewalls=null, updaterooms=null
     */
    update(updateroomconfiguration = true, updatecorners = null) {
        if (!this.__updatesOn) {
            return;
        }
        if (updatecorners != null) {
            updatecorners.forEach((corner) => {
                corner.updateAngles();
            });
        }
        if (!updateroomconfiguration) {
            
            this.dispatchEvent({ type: EVENT_UPDATED, item: this });
            return;
        }

        var scope = this;
        this.walls.forEach((wall) => {
            wall.resetFrontBack();
        });

        let iconsA = [];
        this.icons.forEach((icon) => {
            let icons = new Icon(icon,icon.x,icon.y,icon.id);
            iconsA.push(icons);
        });

        this.icons = iconsA;

        let labelN = [];
        this.labels.forEach((label) => {
            let mlabel = new Label(label,label.x,label.y,label.id);
            labelN.push(mlabel);
        });
        this.labels = labelN;

        let arrowN = [];
        this.arrows.forEach((arrow) => {
            let marrow = new Arrow(arrow.start,arrow.end,arrow.id);
            arrowN.push(marrow);
        });
        this.arrows = arrowN;

        //Destroy current room objects
        this.rooms.forEach((room) => {
            room.destroy();
        });

  
        var roomCorners = this.findRooms(this.corners);
        this.rooms = [];

        this.corners.forEach((corner) => {
            corner.clearAttachedRooms();
            corner.updateAngles();
        });

        // console.log('roomCorners :',roomCorners);
    
        roomCorners.forEach((corners) => {
            var room = new Room(scope, corners);
            room.updateArea();
            scope.rooms.push(room);
            if (scope.metaroomsdata) {
                if (scope.metaroomsdata[room.roomByCornersId]) {
                    let roomType = scope.metaroomsdata[room.roomByCornersId]['roomType'];
                    let colorCode = scope.metaroomsdata[room.roomByCornersId]['baseColor'];
                    room.name = scope.metaroomsdata[room.roomByCornersId]['name'];
                    room.factor = scope.metaroomsdata[room.roomByCornersId]['factor'];
                    room.roomType = roomType;
                    room.baseColor = colorCode;
                    room.code = scope.metaroomsdata[room.roomByCornersId]['code'];
                    room.isDelete = scope.metaroomsdata[room.roomByCornersId]['isDelete'];
                } else {
                    
                    let roomCornerArray = room.getRoomCornerId();
                    for (const [key, value] of Object.entries(scope.metaroomsdata)) {
                        let keyArray = scope.metaroomsdata[key]['cornerId'];
                        
                        let difference = roomCornerArray.filter(x => !keyArray.includes(x));
                        // console.log('difference1 :',difference.length)
                        if(difference.length < 3){
                            // console.log('roomCornerArray :',JSON.stringify(roomCornerArray))
                            // console.log('keyArray :',JSON.stringify(keyArray))
                            // console.log('difference2 :',difference.length)
                            // console.log('difference3 :',difference)
                            // console.log('value.center',value.center);
                            // console.log('room.areaCenter',room.areaCenter);
                            if(room.areaCenter_round.equals(value.center)){
                                let roomType = scope.metaroomsdata[key]['roomType'];
                                let colorCode = scope.metaroomsdata[key]['baseColor'];
                                
                                room.name = scope.metaroomsdata[key]['name'];
                                
                                room.factor = scope.metaroomsdata[key]['factor'];
                                room.roomType = roomType;
                                room.baseColor = colorCode;
                                room.code = scope.metaroomsdata[key]['code'];
                                room.isDelete = scope.metaroomsdata[key]['isDelete'];
                            }
                        }
                      }
                }
            }
        });
        this.metaroomsdata = {};
        this.rooms.forEach((room)=>{
            scope.metaroomsdata[room.roomByCornersId] = {};
            scope.metaroomsdata[room.roomByCornersId]['name'] = room.name;
            scope.metaroomsdata[room.roomByCornersId]['factor'] = room.factor;
            scope.metaroomsdata[room.roomByCornersId]['roomType'] = room.roomType;
            scope.metaroomsdata[room.roomByCornersId]['baseColor'] = room.baseColor;
            scope.metaroomsdata[room.roomByCornersId]['code'] = room.code;
            scope.metaroomsdata[room.roomByCornersId]['isDelete'] = room.isDelete;
            scope.metaroomsdata[room.roomByCornersId]['center'] = room.areaCenter_round;
            scope.metaroomsdata[room.roomByCornersId]['cornerId'] = room.getRoomCornerId();
        });
        this.__orphanWalls = this.assignOrphanEdges();
        this.__cornerGroups.createGroups();
        this.dispatchEvent({ type: EVENT_UPDATED, item: this });
        this.dispatchEvent({ type: EVENT_NEW_ROOMS_ADDED, item: this });
    }

    /**
     * Find the "rooms" in our planar straight-line graph. Rooms are set of the
     * smallest (by area) possible cycles in this graph. The room corners are always
     * ordered in clockwise direction
     * 
     * @param corners
     *            The corners of the floorplan.
     * @returns The rooms, each room as an array of corners.
     * @param {Corners[]}
     *            corners
     * @return {Corners[][]} loops
     */
    findRooms(corners) {

        function _calculateTheta(previousCorner, currentCorner, nextCorner) {
            var theta = Utils.angle2pi(new Vector2(previousCorner.x - currentCorner.x, previousCorner.y - currentCorner.y), new Vector2(nextCorner.x - currentCorner.x, nextCorner.y - currentCorner.y));
            return theta;
        }

        function _removeDuplicateRooms(roomArray) {
            var results = [];
            var lookup = {};
            var hashFunc = function (corner) {
                return corner.id;
            };
            var sep = '-';
            for (var i = 0; i < roomArray.length; i++) {
                // rooms are cycles, shift it around to check uniqueness
                var add = true;
                var room = roomArray[i];
                for (var j = 0; j < room.length; j++) {
                    var roomShift = Utils.cycle(room, j);
                    var str = Utils.map(roomShift, hashFunc).join(sep);
                    
                    if (lookup.hasOwnProperty(str)) {
                        add = false;
                    }
                }
                if (add) {
                    results.push(roomArray[i]);
                    lookup[str] = true;
                }
            }
            return results;
        }

        /**
         * An internal method to find rooms based on corners and their
         * connectivities
         */
        function _findTightestCycle(firstCorner, secondCorner) {
            var stack = [];
            var next = { corner: secondCorner, previousCorners: [firstCorner] };
            var visited = {};
            visited[firstCorner.id] = true;

            while (next) {
                // update previous corners, current corner, and visited corners
                var currentCorner = next.corner;
                visited[currentCorner.id] = true;

                // did we make it back to the startCorner?
                if (next.corner === firstCorner && currentCorner !== secondCorner) {
                    return next.previousCorners;
                }

                var addToStack = [];
                var adjacentCorners = next.corner.adjacentCorners();
                for (var i = 0; i < adjacentCorners.length; i++) {
                    var nextCorner = adjacentCorners[i];

                    // is this where we came from?
                    // give an exception if its the first corner and we aren't
                    // at the second corner
                    if (nextCorner.id in visited && !(nextCorner === firstCorner && currentCorner !== secondCorner)) {
                        continue;
                    }

                    // nope, throw it on the queue
                    addToStack.push(nextCorner);
                }

                var previousCorners = next.previousCorners.slice(0);
                previousCorners.push(currentCorner);
                if (addToStack.length > 1) {
                    // visit the ones with smallest theta first
                    var previousCorner = next.previousCorners[next.previousCorners.length - 1];
                    addToStack.sort(function (a, b) { 
                        return (_calculateTheta(previousCorner, currentCorner, b) - _calculateTheta(previousCorner, currentCorner, a)); 
                    });
                }

                if (addToStack.length > 0) {
                    // add to the stack
                    addToStack.forEach((corner) => {
                        stack.push({ corner: corner, previousCorners: previousCorners });
                    });
                }

                // pop off the next one
                next = stack.pop();
            }
            return [];
        }

        // find tightest loops, for each corner, for each adjacent
        // TODO: optimize this, only check corners with > 2 adjacents, or
        // isolated cycles
        var loops = [];

        corners.forEach((firstCorner) => {
            firstCorner.adjacentCorners().forEach((secondCorner) => {
                loops.push(_findTightestCycle(firstCorner, secondCorner));
            });
        });

        // remove duplicates
        var uniqueLoops = _removeDuplicateRooms(loops);
        // remove CW loops
        var uniqueCCWLoops = Utils.removeIf(uniqueLoops, Utils.isClockwise);
        return uniqueCCWLoops;
    }

    /**
     * @param {CarbonSheet}
     *            val
     */
    set carbonSheet(val) {
        this._carbonSheet = val;
    }

    /**
     * @return {CarbonSheet} _carbonSheet reference to the instance of
     *         {@link CarbonSheet}
     */
    get carbonSheet() {
        return this._carbonSheet;
    }

    get roofPlanesForIntersection() {
        return this.__roofPlanesForIntersection;
    }

    get floorPlanesForIntersection() {
        return this.__floorPlanesForIntersection;
    }

    get wallPlanesForIntersection() {
        return this.__wallPlanesForIntersection;
    }

    get cornerGroups() {
        return this.__cornerGroups;
    }

    get externalCorners() {
        return this.__externalCorners;
    }

    get externalWalls() {
        return this.__externalWalls;
    }

    get externalRooms() {
        return this.__externalRooms;
    }

    // get findRooms(){
    //     return this.findRooms(this.corners);
    // }

    get boundary() {
        return this.__boundary;
    }

    get orphanWalls(){
        return this.__orphanWalls;
    }
}
export default Floorplan;